import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userConfigKeys } from "./user-config.options";
import { userConfigService } from "./user-config.service";

// ==================== MUTATION OPTIONS ====================

/**
 * Mutation for setting/updating a configuration value
 */
export function useSetConfigurationValueMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			key,
			value,
		}: {
			userId: string;
			key: string;
			value: string;
		}) => userConfigService.setConfigurationValue(userId, key, value),
		onSuccess: (_data, variables) => {
			// Invalidate the user's configuration values to ensure fresh data
			queryClient.invalidateQueries({
				queryKey: userConfigKeys.values(variables.userId),
			});
		},
		onError: (error) => {
			console.error("Failed to set configuration value:", error);
		},
	});
}

/**
 * Mutation for deleting a configuration value
 */
export function useDeleteConfigurationValueMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, key }: { userId: string; key: string }) =>
			userConfigService.deleteConfigurationValue(userId, key),
		onSuccess: (_data, variables) => {
			// Invalidate the user's configuration values to ensure fresh data
			queryClient.invalidateQueries({
				queryKey: userConfigKeys.values(variables.userId),
			});
		},
		onError: (error) => {
			console.error("Failed to delete configuration value:", error);
		},
	});
}

/**
 * Mutation with optimistic update for setting a configuration value
 * Use this when you want immediate UI feedback
 */
export function useSetConfigurationValueOptimistic() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			key,
			value,
		}: {
			userId: string;
			key: string;
			value: string;
		}) => userConfigService.setConfigurationValue(userId, key, value),

		onMutate: async ({ userId, key, value }) => {
			// Cancel any outgoing refetches to avoid overwriting optimistic update
			await queryClient.cancelQueries({
				queryKey: userConfigKeys.values(userId),
			});

			// Snapshot the previous values
			const previousValues = queryClient.getQueryData(
				userConfigKeys.values(userId),
			);

			// Optimistically update the cache
			queryClient.setQueryData(
				userConfigKeys.values(userId),
				(
					old: Array<{ key: string | null; value: string | null }> | undefined,
				) => {
					if (!old) return [{ key, value }];

					// Find existing entry or add new one
					const existingIndex = old.findIndex((item) => item.key === key);
					if (existingIndex >= 0) {
						const updated = [...old];
						updated[existingIndex] = { key, value };
						return updated;
					}
					return [...old, { key, value }];
				},
			);

			return { previousValues };
		},

		onError: (_error, variables, context) => {
			// Restore previous values on error
			if (context?.previousValues) {
				queryClient.setQueryData(
					userConfigKeys.values(variables.userId),
					context.previousValues,
				);
			}
		},

		onSettled: (_data, _error, variables) => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: userConfigKeys.values(variables.userId),
			});
		},
	});
}

// ==================== BULK OPERATIONS ====================

/**
 * Mutation for setting multiple configuration values at once
 */
export function useSetConfigurationValuesBulkMutation() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			userId,
			configs,
		}: {
			userId: string;
			configs: Array<{ key: string; value: string }>;
		}) => userConfigService.setConfigurationValues(userId, configs),
		onSuccess: (_data, variables) => {
			// Invalidate the user's configuration values
			queryClient.invalidateQueries({
				queryKey: userConfigKeys.values(variables.userId),
			});
		},
		onError: (error) => {
			console.error("Failed to bulk set configuration values:", error);
		},
	});
}

// ==================== UTILITY HOOKS ====================

/**
 * Hook that returns mutation utilities for user configuration
 * Provides access to all mutation functions in one place
 */
export function useUserConfigMutations() {
	const setMutation = useSetConfigurationValueMutation();
	const setOptimistic = useSetConfigurationValueOptimistic();
	const deleteMutation = useDeleteConfigurationValueMutation();
	const bulkSetMutation = useSetConfigurationValuesBulkMutation();

	return {
		set: setMutation,
		setOptimistic,
		delete: deleteMutation,
		bulkSet: bulkSetMutation,
		// Helper methods
		isLoading:
			setMutation.isPending ||
			setOptimistic.isPending ||
			deleteMutation.isPending ||
			bulkSetMutation.isPending,
		isError:
			setMutation.isError ||
			setOptimistic.isError ||
			deleteMutation.isError ||
			bulkSetMutation.isError,
	};
}
